import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { RebelColors, RebelContainer, RebelContentWrapper, RebelClientCard, RebelAccentButton, RebelSecondaryButton } from "../../../styles/rebel-experiences";
import { GetDB, generateGUID } from "../../../utils/database/firebase-budget-tool";
import { listenToCollection, listenToDocument, setDocument, updateDocumentProperty } from "../../../utils/firestore-functions";
import { getVoterCookie, updateVoterCookie } from "../../../utils/ranking-cookies";
import RebelBudgetConfig from "./rebel-budget-config";
import RebelBudgetSummary from "./rebel-budget-summary";
import RebelBudgetPropertyTab from "./rebel-budget-property-tab";
import RebelBudgetAddModal from "./rebel-budget-add-modal";
import RebelBudgetSaveModal from "./rebel-budget-save-modal";

const RebelBudget = ({ collapsed }) => {
    const { id } = useParams();
    const budgetDB = GetDB();

    // Trip configuration state
    const [config, setConfig] = useState({
        companyName: "",
        startDate: "",
        endDate: "",
        attendees: 8,
        currency: "USD$",
    });

    // Properties state
    const [properties, setProperties] = useState([]); // array of villa IDs
    const [villaList, setVillaList] = useState([]); // all available villas
    const [villaData, setVillaData] = useState({}); // villaId -> full villa data
    const [categories, setCategories] = useState([]); // agenda-block-types

    // Tab state
    const [tabKey, setTabKey] = useState(0); // 0 = summary, 1+ = property tabs

    // Ranking state
    const [selectedRankings, setSelectedRankings] = useState({});
    const [savedRankings, setSavedRankings] = useState({});
    const [allRankings, setAllRankings] = useState({});
    const [voterId, setVoterId] = useState(null);

    // Save state
    const [budgetId, setBudgetId] = useState(id || null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [isLoaded, setIsLoaded] = useState(!id); // if no id, ready immediately

    // Calculate trip length from dates
    const tripLength = (() => {
        if (!config.startDate || !config.endDate) return 0;
        const start = new Date(config.startDate);
        const end = new Date(config.endDate);
        const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        return diff > 0 ? diff : 0;
    })();

    // Load all available villas
    useEffect(() => {
        const unsubscribe = listenToCollection(budgetDB, "rebelVillas", (villas) => {
            setVillaList(villas);
        });
        return unsubscribe;
    }, []);

    // Load agenda-block-types for category names
    useEffect(() => {
        const unsubscribe = listenToCollection(budgetDB, "agenda-block-types", (cats) => {
            setCategories(cats);
        });
        return unsubscribe;
    }, []);

    // Load saved session if id is provided
    useEffect(() => {
        if (!id) return;

        const unsubscribe = listenToDocument(budgetDB, "rebelClientBudgets", id, (data) => {
            if (data) {
                setConfig({
                    companyName: data.companyName || "",
                    startDate: data.startDate || "",
                    endDate: data.endDate || "",
                    attendees: data.attendees || 8,
                    currency: data.currency || "USD$",
                });
                setProperties(data.properties || []);
                setAllRankings(data.rankings || {});
                setBudgetId(id);
                setIsLoaded(true);

                // Load villa data for each saved property
                if (data.properties && data.properties.length > 0) {
                    data.properties.forEach(villaId => {
                        loadVillaData(villaId);
                    });
                }
            } else {
                setIsLoaded(true);
            }
        });

        return unsubscribe;
    }, [id]);

    // Initialize voter from cookie
    useEffect(() => {
        const cookieId = budgetId || "rebel-budget-new";
        const voterData = getVoterCookie(cookieId);
        setVoterId(voterData.voterId);
        setSelectedRankings(voterData.lastSubmitted || {});
        setSavedRankings(voterData.lastSubmitted || {});
    }, [budgetId]);

    // Load villa data by ID
    const loadVillaData = (villaId) => {
        const villa = villaList.find(v => v.id === villaId || v._docId === villaId);
        if (villa) {
            setVillaData(prev => ({ ...prev, [villaId]: villa }));
        } else {
            // Villa not yet in list, listen for it directly
            listenToDocument(budgetDB, "rebelVillas", villaId, (data) => {
                if (data) {
                    setVillaData(prev => ({ ...prev, [villaId]: data }));
                }
            });
        }
    };

    // When villaList loads, populate villaData for existing properties
    useEffect(() => {
        if (villaList.length > 0 && properties.length > 0) {
            properties.forEach(villaId => {
                if (!villaData[villaId]) {
                    const villa = villaList.find(v => v.id === villaId || v._docId === villaId);
                    if (villa) {
                        setVillaData(prev => ({ ...prev, [villaId]: villa }));
                    }
                }
            });
        }
    }, [villaList, properties]);

    // Add a property
    const handleAddProperty = (villaId) => {
        if (!properties.includes(villaId)) {
            setProperties(prev => [...prev, villaId]);
            loadVillaData(villaId);
            setShowAddModal(false);
            // Switch to the new property's tab
            setTabKey(properties.length + 1);
        }
    };

    // Remove a property
    const handleRemoveProperty = (villaId) => {
        const idx = properties.indexOf(villaId);
        setProperties(prev => prev.filter(id => id !== villaId));
        setVillaData(prev => {
            const next = { ...prev };
            delete next[villaId];
            return next;
        });
        // Remove any rankings for this property
        setSelectedRankings(prev => {
            const next = { ...prev };
            delete next[villaId];
            return next;
        });
        // Switch to summary tab
        setTabKey(0);
    };

    // Handle config change
    const handleConfigChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    // Handle ranking change
    const handleRankingChange = (propertyId, rank) => {
        setSelectedRankings(prev => {
            const next = { ...prev };
            if (rank === null) {
                delete next[propertyId];
            } else {
                next[propertyId] = rank;
            }
            return next;
        });
    };

    // Get currency symbol from currency string (e.g., "USD$" -> "$")
    const currencySymbol = config.currency.slice(3);

    return (
        <RebelContainer>
            <RebelContentWrapper>
                <RebelClientCard>
                    <HeaderRow>
                        <HeaderTitle>R.E.B.E.L Property Comparison</HeaderTitle>
                        {config.companyName && <CompanyName>{config.companyName}</CompanyName>}
                    </HeaderRow>

                    <RebelBudgetConfig
                        config={config}
                        onChange={handleConfigChange}
                        tripLength={tripLength}
                    />

                    {/* Tab navigation */}
                    <TabRow>
                        {tabKey !== 0 ? (
                            <TabButton onClick={() => setTabKey(0)}>
                                <TabTitle>Summary</TabTitle>
                            </TabButton>
                        ) : (
                            <ActiveTabButton>
                                <TabTitle>Summary</TabTitle>
                            </ActiveTabButton>
                        )}

                        {properties.map((villaId, i) => {
                            const villa = villaData[villaId];
                            const name = villa?.villaName || villa?.name || `Property ${i + 1}`;
                            const location = villa?.location || "";
                            const tabIdx = i + 1;

                            return tabKey !== tabIdx ? (
                                <TabButton key={villaId} onClick={() => setTabKey(tabIdx)}>
                                    <TabTitle>{name}</TabTitle>
                                    {location && <TabSubtitle>{location}</TabSubtitle>}
                                </TabButton>
                            ) : (
                                <ActiveTabButton key={villaId}>
                                    <TabTitle>{name}</TabTitle>
                                    {location && <TabSubtitle active>{location}</TabSubtitle>}
                                </ActiveTabButton>
                            );
                        })}

                        <TabButton onClick={() => setShowAddModal(true)}>
                            <TabTitle>+</TabTitle>
                        </TabButton>
                    </TabRow>

                    {/* Tab content */}
                    <TabContent>
                        {tabKey === 0 && (
                            <RebelBudgetSummary
                                properties={properties}
                                villaData={villaData}
                                categories={categories}
                                config={config}
                                tripLength={tripLength}
                                currencySymbol={currencySymbol}
                                allRankings={allRankings}
                                selectedRankings={selectedRankings}
                                onRankingChange={handleRankingChange}
                                savedRankings={savedRankings}
                                setSavedRankings={setSavedRankings}
                                voterId={voterId}
                                budgetId={budgetId}
                                budgetDB={budgetDB}
                            />
                        )}

                        {properties.map((villaId, i) => {
                            if (tabKey === i + 1) {
                                return (
                                    <RebelBudgetPropertyTab
                                        key={villaId}
                                        villaId={villaId}
                                        villa={villaData[villaId]}
                                        categories={categories}
                                        config={config}
                                        tripLength={tripLength}
                                        currencySymbol={currencySymbol}
                                        onRemove={() => handleRemoveProperty(villaId)}
                                    />
                                );
                            }
                            return null;
                        })}
                    </TabContent>

                    {/* Save button */}
                    <SaveRow>
                        <RebelAccentButton onClick={() => setShowSaveModal(true)}>
                            Save & Share
                        </RebelAccentButton>
                    </SaveRow>

                    {/* Modals */}
                    <RebelBudgetAddModal
                        isOpen={showAddModal}
                        toggle={() => setShowAddModal(false)}
                        villaList={villaList}
                        existingProperties={properties}
                        onAdd={handleAddProperty}
                    />

                    <RebelBudgetSaveModal
                        isOpen={showSaveModal}
                        toggle={() => setShowSaveModal(false)}
                        config={config}
                        properties={properties}
                        rankings={allRankings}
                        budgetId={budgetId}
                        setBudgetId={setBudgetId}
                        budgetDB={budgetDB}
                    />
                </RebelClientCard>
            </RebelContentWrapper>
        </RebelContainer>
    );
};

export default RebelBudget;

const HeaderRow = styled.div`
    text-align: center;
    margin-bottom: 1.5rem;
`;

const HeaderTitle = styled.h1`
    color: ${RebelColors.gold};
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    letter-spacing: 2px;
`;

const CompanyName = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 1.1rem;
    font-style: italic;
    margin-bottom: 0;
`;

const TabRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-top: 1.5rem;
`;

const TabButton = styled.button`
    background-color: ${RebelColors.surface};
    border: 2px solid ${RebelColors.border};
    border-radius: 10px 10px 0 0;
    margin: 0 1px;
    padding: 8px 20px;
    min-height: 52px;
    color: ${RebelColors.text};
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    vertical-align: bottom;

    &:hover {
        background-color: ${RebelColors.surfaceLight};
        color: ${RebelColors.gold};
        border-color: ${RebelColors.gold};
    }

    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
    }
`;

const ActiveTabButton = styled.button`
    background-color: ${RebelColors.gold};
    color: #000000;
    border: 2px solid ${RebelColors.gold};
    border-radius: 10px 10px 0 0;
    margin: 0 1px -1px 1px;
    padding: 8px 20px;
    min-height: 52px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    vertical-align: bottom;

    &:hover {
        background-color: ${RebelColors.gold};
        color: #000000;
    }

    &:focus {
        outline: none;
    }
`;

const TabTitle = styled.div`
    line-height: 1.2;
`;

const TabSubtitle = styled.div`
    font-size: 11px;
    font-weight: 400;
    margin-top: 2px;
    opacity: 0.8;
    color: ${props => props.active ? '#000000' : RebelColors.primaryMuted};
    line-height: 1.2;
`;

const TabContent = styled.div`
    margin-left: 1px;
    margin-right: 1px;
    padding: 15px;
    border-radius: 0 0 10px 10px;
    border: 2px solid rgba(255, 215, 0, 0.3);
    background-color: rgba(20, 20, 20, 0.7);
`;

const SaveRow = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
`;
